/**
 * viz のフレーム（VizState.value は unknown）を盤面に描ける形に読む。読めない値は文字列にする。
 */
import type { VizState } from '../viz';

export type Cell = number | string;

function cell(v: unknown): Cell {
  if (typeof v === 'number' || typeof v === 'string') return v;
  if (v === null) return 'nil';
  return JSON.stringify(v);
}

/** 配列・スタック・キュー・Set の要素。配列でなければ空。 */
export function cellsOf(state: VizState | undefined): Cell[] {
  return state && Array.isArray(state.value) ? state.value.map(cell) : [];
}

/** 辞書の `[[key, value], ...]`。形が違う要素は飛ばす。 */
export function pairsOf(state: VizState | undefined): [string, Cell][] {
  if (!state || !Array.isArray(state.value)) return [];
  const out: [string, Cell][] = [];
  for (const p of state.value) {
    if (Array.isArray(p) && p.length === 2) out.push([String(cell(p[0])), cell(p[1])]);
  }
  return out;
}

/** フレームの 1 変数を文章にする（盤面の visually-hidden と、一致しなかったときの表示）。 */
export function describeState(label: string, state: VizState | undefined): string {
  if (!state) return `${label}: まだ届いていません`;
  switch (state.kind) {
    case 'dict': {
      const pairs = pairsOf(state);
      return pairs.length === 0
        ? `${label}: 空`
        : `${label}: ${pairs.map(([k, v]) => `${k} = ${v}`).join('、')}`;
    }
    case 'queue': {
      const cells = cellsOf(state);
      return `${label}: [${cells.join(', ')}]、head = ${state.head ?? 0}`;
    }
    case 'stack':
    case 'array':
    case 'set':
      return `${label}: [${cellsOf(state).join(', ')}]`;
    default:
      return `${label}: ${String(cell(state.value))}`;
  }
}
